import cn from "classnames"
import { lazy, Suspense } from "react"

import { Col, Flex, Row, Text } from "@/atoms"
import { isCodeIssue, isMaliciousPackageIssue } from "@/domain/issue"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { DescriptionIssueCard } from "@/molecules/DescriptionIssueCard"
import { useIssue } from "@/organisms/Drawers/applications/triage/useTriage"
import { RuntimeEvidenceCard } from "@/organisms/Drawers/components/RuntimeEvidenceTab/RuntimeEvidenceCard"
import { QUICK_WINS_DRAWER_ID } from "@/organisms/Drawers/const"
import { QuickWinIssueBanners } from "@/pages/TriagePage/components/molecules/QuickWinIssueBanners"
import { ActionCard } from "../ActionsCard"
import { MetaIssue } from "../MetaIssue"
import { TARGET_RISK_ID } from "../MitigationDemoCards/MitigationDemoCards.const"
import { PackageVulnerabilityCards } from "../PackageVulnerabilityCards"
import { ScoreCardExplanation } from "../ScoreCardExplanation"
import { SummaryCard } from "../SummaryCard"
import { VulnerableCodeCards } from "../VulnerableCodeCards"

import styles from "./OverviewTabTriageDrawer.module.scss"

import type { TabComponentProps } from "@/organisms/Drawers/components/TabsDrawerTemplate"
import { ImpactCard } from "../ImpactCard"
import { InsightsCard } from "../InsightsCard"

// Demo-only; lazy-loaded so the proprietary demo UI (and its react-diff-view /
// react-markdown deps) stays out of the main bundle.
const MitigationDemoSection = lazy(() =>
  import("../MitigationDemoCards/MitigationDemoSection").then((module) => ({
    default: module.MitigationDemoSection
  }))
)

export const OverviewTabTriageDrawer = (props: TabComponentProps) => {
  const issueId = props.record?.id
  const { response, isLoading } = useIssue(issueId)
  const record = response?.data?.[0]
  const { t } = useTranslation()
  const isInternalTenant = useFeatureFlags("isInternalTenant")

  if (isLoading || !record) return null

  const isVulnerableCode = isCodeIssue(record)
  const isMaliciousPackage = isMaliciousPackageIssue(record)
  const isMitigationDemo =
    !!isInternalTenant && record.riskId === TARGET_RISK_ID
  const summaryCardSpan = isMaliciousPackage ? 15 : 14

  return (
    <>
      <MetaIssue record={record} />
      {record.hasQuickWins &&
        props.previousDrawerId?.charAt(0) !== QUICK_WINS_DRAWER_ID && (
          <QuickWinIssueBanners
            issueId={record.id}
            projectId={record.projectId}
          />
        )}
      <Row gutter={[0, 16]} className={styles.rowContainer}>
        <Row
          gutter={[0, 16]}
          className={cn(
            styles.rowWrapper,
            !record.ruleId && styles.rowWrapperPadding
          )}
        >
          {(record.tickets?.length > 0 || record.pullRequests?.length > 0) && (
            <Col span={24}>
              <ActionCard.Collapse
                tickets={record.tickets}
                ticketSystems={record.ticketSystems}
                pullRequests={record.pullRequests}
                issueId={record.id}
              />
            </Col>
          )}
          <Col span={24}>
            <Row gutter={[16, 0]}>
              <Col span={summaryCardSpan}>
                <SummaryCard record={record} />
              </Col>
              <Col span={24 - summaryCardSpan}>
                <Flex
                  vertical
                  gap={16}
                  className={styles.scoreAndRuntimeWrapper}
                >
                  <ScoreCardExplanation record={record} />
                  {!isMaliciousPackage && (
                    <RuntimeEvidenceCard
                      {...props}
                      record={record}
                      isCodeIssue={isVulnerableCode}
                      eventFrom="overviewLink"
                    />
                  )}
                  {isMaliciousPackage && <InsightsCard record={record} />}
                </Flex>
              </Col>
            </Row>
          </Col>
          {record.status != "processing" && (
            <DescriptionIssueCard t={t} record={record.findings[0]} />
          )}
          {!isVulnerableCode && <ImpactCard issue={record} />}
          {isMitigationDemo ? (
            <Suspense fallback={null}>
              <MitigationDemoSection record={record} />
            </Suspense>
          ) : isVulnerableCode ? (
            <VulnerableCodeCards record={record} drawerId={props.drawerId} />
          ) : (
            <PackageVulnerabilityCards
              record={record}
              classNameCol={styles.fixesCard}
            />
          )}
        </Row>
        {record.ruleId && (
          <Text className={styles.ruleId} type="secondary">
            {t("triage.drawer.ruleId", { ruleId: record.ruleId })}
          </Text>
        )}
      </Row>
    </>
  )
}
